import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios, { AxiosError } from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Avatar,
  Paper,
  Skeleton,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import GroupIcon from "@mui/icons-material/Group";
import BusinessIcon from "@mui/icons-material/Business";
import TimelineIcon from "@mui/icons-material/Timeline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useForm, Controller } from "react-hook-form";
import { baseUrl } from "../../constants";

/** ========== Types ========== */
type AssignmentType =
  | "USER"
  | "ROLE"
  | "POSITION"
  | "MANAGER_OF_REQUESTER"
  | "DEPARTMENT_MANAGERS"
  | "PROJECT_MANAGERS";

type AssignmentStep = {
  id: number;
  assignmentRuleId: number;
  stepOrder: number;
  assignmentType: AssignmentType;
  targetUserId?: number | null;
  targetRole?: string | null;
  targetPositionId?: number | null;
  sameDepartmentAsRequester: boolean;
  useTaskProject: boolean;
  useProjectDepartment: boolean;
  autoAdvanceIfNone: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  targetUser?: {
    id: number;
    username?: string;
    profile?: { firstName?: string; lastName?: string };
  };
  targetPosition?: { id: number; name?: string; title?: string };
};

type AssignmentRule = {
  id: number;
  workspaceId: number;
  taskType: string;
  approvalPolicyId: number;
  name?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  approvalPolicy?: { id: number; name: string; taskType: string };
  steps: AssignmentStep[];
};

type AssignmentRuleForm = {
  taskType: string;
  approvalPolicyId: number | string;
  name?: string;
  isActive?: boolean;
};

type AssignmentStepForm = {
  stepOrder: number | string;
  assignmentType: AssignmentType;
  targetUserId?: number | string | null;
  targetUserIds?: number[] | string[];
  targetRole?: string | null;
  targetPositionId?: number | string | null;
  sameDepartmentAsRequester?: boolean;
  useTaskProject?: boolean;
  useProjectDepartment?: boolean;
  autoAdvanceIfNone?: boolean;
  isActive?: boolean;
};

type ApprovalPolicy = {
  id: number;
  name: string;
  taskType: string;
  isActive: boolean;
};

type UserLite = {
  id: number;
  username?: string;
  name?: string;
  email?: string;
  profile?: {
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    imageUrl?: string;
  };
};

type Position = {
  id: number;
  name?: string;
  title?: string;
  departmentId?: number;
};

/** ========== Utils ========== */
const tokenHeader = () => {
  const t =
    localStorage.getItem("accessToken") || localStorage.getItem("token");
  return { Authorization: `Bearer ${t}` };
};

const toInt = (v: any): number | undefined =>
  v === "" || v === null || v === undefined
    ? undefined
    : Number.parseInt(String(v), 10);

const userLabel = (u?: UserLite | null): string => {
  if (!u) return "";
  const name =
    u.profile?.name ||
    (u.profile?.firstName || u.profile?.lastName
      ? `${u.profile?.firstName || ""} ${u.profile?.lastName || ""}`.trim()
      : "");
  return name || u.username || u.profile?.email || `User #${u.id}`;
};

const posLabel = (p?: Position | null) =>
  (p?.name || p?.title || `Position #${p?.id ?? ""}`).toString();

const avatarInitials = (u?: UserLite | null) => {
  const n = userLabel(u);
  if (!n) return "";
  const parts = n.split(" ").filter(Boolean);
  return (
    (parts[0]?.[0] ?? "").toUpperCase() + (parts[1]?.[0] ?? "").toUpperCase()
  );
};

/** ========== Step Card Component ========== */
type StepCardProps = {
  step: AssignmentStep;
  idx: number;
  total: number;
  dicts: {
    users: Map<number, UserLite>;
    positions: Map<number, Position>;
  };
  onMove: (index: number, dir: -1 | 1) => void;
  onEdit: (step: AssignmentStep) => void;
  onDelete: (id: number) => void;
};

const StepCard = React.memo(function StepCard({
  step: s,
  idx,
  total,
  dicts,
  onMove,
  onEdit,
  onDelete,
}: StepCardProps) {
  const header = (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Chip
        size="small"
        label={`Step ${s.stepOrder}`}
        icon={<TimelineIcon fontSize="small" />}
      />
      <Chip
        size="small"
        label={s.assignmentType}
        color="primary"
        icon={
          s.assignmentType === "USER" ? (
            <PersonIcon />
          ) : s.assignmentType === "POSITION" ? (
            <WorkIcon />
          ) : s.assignmentType === "ROLE" ? (
            <GroupIcon />
          ) : (
            <BusinessIcon />
          )
        }
      />
      {s.autoAdvanceIfNone && (
        <Chip size="small" label="Auto-advance if none" variant="outlined" />
      )}
    </Stack>
  );

  const u = s.targetUserId ? dicts.users.get(s.targetUserId) : undefined;
  const p = s.targetPositionId
    ? dicts.positions.get(s.targetPositionId)
    : undefined;

  const details = (
    <Stack spacing={1} mt={1}>
      {s.assignmentType === "USER" && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ width: 28, height: 28 }}>{avatarInitials(u)}</Avatar>
          <Typography variant="body2">
            <b>Assignee:</b> {userLabel(u) || `User #${s.targetUserId}`}
          </Typography>
        </Stack>
      )}
      {s.assignmentType === "POSITION" && (
        <Typography variant="body2">
          <b>Position:</b> {posLabel(p) || `Position #${s.targetPositionId}`}
        </Typography>
      )}
      {s.assignmentType === "ROLE" && (
        <Typography variant="body2">
          <b>Role:</b> {s.targetRole || "-"}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip
          size="small"
          label={`useTaskProject: ${s.useTaskProject ? "Yes" : "No"}`}
        />
        <Chip
          size="small"
          label={`useProjectDepartment: ${
            s.useProjectDepartment ? "Yes" : "No"
          }`}
        />
        {s.sameDepartmentAsRequester && (
          <Chip size="small" label="sameDepartmentAsRequester" />
        )}
      </Stack>
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
      >
        <Box flex={1}>
          {header}
          {details}
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Move up">
            <span>
              <IconButton disabled={idx === 0} onClick={() => onMove(idx, -1)}>
                <ArrowUpwardIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton
                disabled={idx === total - 1}
                onClick={() => onMove(idx, +1)}
              >
                <ArrowDownwardIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton onClick={() => onEdit(s)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton color="error" onClick={() => onDelete(s.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
});

/** ========== Main Component ========== */
export default function AssignmentSettingsPage() {
  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  const [selectedRule, setSelectedRule] = useState<AssignmentRule | null>(null);

  const [policies, setPolicies] = useState<ApprovalPolicy[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [users, setUsers] = useState<UserLite[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  // User filter states
  const [userFilterType, setUserFilterType] = useState<
    "all" | "department" | "project" | "position"
  >("all");
  const [userFilterDepartmentId, setUserFilterDepartmentId] = useState<
    number | null
  >(null);
  const [userFilterProjectId, setUserFilterProjectId] = useState<number | null>(
    null
  );
  const [userFilterPositionId, setUserFilterPositionId] = useState<
    number | null
  >(null);

  const [busy, setBusy] = useState(false);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [openStepDialog, setOpenStepDialog] = useState<{
    open: boolean;
    stepId?: number;
  }>({ open: false });
  const [userSearch, setUserSearch] = useState("");
  const [viewTab, setViewTab] = useState<"list" | "diagram">("list");

  // Caches for performance
  const usersCache = new Map<number, UserLite>();
  const positionsCache = new Map<number, Position>();

  const notify = useCallback(
    (message: string, severity: "success" | "error" | "info" = "success") => {
      setSnack({ open: true, message, severity });
    },
    []
  );

  const handleErr = useCallback(
    (err: unknown, fallback = "Something went wrong") => {
      const msg =
        (err as AxiosError)?.response?.data &&
        typeof (err as AxiosError).response?.data === "object"
          ? JSON.stringify((err as AxiosError).response?.data)
          : (err as Error)?.message || fallback;
      notify(msg, "error");
      console.error(err);
    },
    [notify]
  );

  /** -------- Load Data -------- */
  const loadRules = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${baseUrl}/assignment-rules`, {
        headers: tokenHeader(),
      });
      setRules(Array.isArray(data) ? data : []);
    } catch (e) {
      handleErr(e, "Failed to load assignment rules");
      setRules([]);
    } finally {
      setBusy(false);
    }
  }, [handleErr]);

  const loadPolicies = useCallback(async () => {
    setLoadingPolicies(true);
    try {
      const { data } = await axios.get(`${baseUrl}/approval-policies`, {
        headers: tokenHeader(),
      });
      setPolicies(Array.isArray(data) ? data : []);
    } catch (e) {
      handleErr(e, "Failed to load approval policies");
      setPolicies([]);
    } finally {
      setLoadingPolicies(false);
    }
  }, [handleErr]);

  const loadPositions = useCallback(async () => {
    setLoadingPositions(true);
    try {
      const { data } = await axios.get(`${baseUrl}/positions`, {
        headers: tokenHeader(),
      });
      setPositions(Array.isArray(data) ? data : []);
    } catch (e) {
      handleErr(e, "Failed to load positions");
      setPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  }, [handleErr]);

  const loadDepartments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/departments`, {
        headers: tokenHeader(),
      });
      setDepartments(Array.isArray(data) ? data : []);
    } catch (e) {
      handleErr(e, "Failed to load departments");
      setDepartments([]);
    }
  }, [handleErr]);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/projects`, {
        headers: tokenHeader(),
      });
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      handleErr(e, "Failed to load projects");
      setProjects([]);
    }
  }, [handleErr]);

  const searchUsers = useCallback(
    async (query: string) => {
      setLoadingUsers(true);
      try {
        const params: any = query ? { name: query, take: 50 } : { take: 50 };

        // Add filter parameters based on selected filter type
        if (userFilterType === "department" && userFilterDepartmentId) {
          params.departmentId = userFilterDepartmentId;
        } else if (userFilterType === "project" && userFilterProjectId) {
          params.projectId = userFilterProjectId;
        } else if (userFilterType === "position" && userFilterPositionId) {
          params.positionId = userFilterPositionId;
        }

        const { data } = await axios.get(`${baseUrl}/users`, {
          headers: tokenHeader(),
          params,
        });
        // Backend returns { users: [], totalCount: number }
        const userList = data?.users || (Array.isArray(data) ? data : data?.items || []);
        setUsers(userList);
      } catch (e) {
        handleErr(e, "Failed to search users");
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    },
    [
      handleErr,
      userFilterType,
      userFilterDepartmentId,
      userFilterProjectId,
      userFilterPositionId,
    ]
  );

  useEffect(() => {
    loadRules();
    loadPolicies();
    loadPositions();
    loadDepartments();
    loadProjects();
  }, [loadRules, loadPolicies, loadPositions, loadDepartments, loadProjects]);

  // Debounced user search - update immediately if query is empty or has content
  useEffect(() => {
    if (userSearch !== "") {
      const timeout = setTimeout(() => {
        searchUsers(userSearch);
      }, 300);
      return () => clearTimeout(timeout);
    } else {
      searchUsers("");
    }
  }, [userSearch, searchUsers]);

  // Reload users when filter changes
  useEffect(() => {
    searchUsers(userSearch);
  }, [
    userFilterType,
    userFilterDepartmentId,
    userFilterProjectId,
    userFilterPositionId,
    searchUsers,
  ]);

  /** -------- Select Rule -------- */
  const selectRule = useCallback(
    async (id: number) => {
      setSelectedRuleId(id);
      setSelectedRule(null);
      setBusy(true);
      try {
        const { data } = await axios.get(`${baseUrl}/assignment-rules/${id}`, {
          headers: tokenHeader(),
        });
        setSelectedRule(data);
        // Hydrate caches
        await hydrateVisibleDictionaries(data.steps || []);
      } catch (e) {
        handleErr(e, "Failed to load rule");
        setSelectedRule(null);
      } finally {
        setBusy(false);
      }
    },
    [handleErr]
  );

  const hydrateVisibleDictionaries = useCallback(
    async (steps: AssignmentStep[]) => {
      const promises: Promise<any>[] = [];
      steps.forEach((s) => {
        if (s.targetUserId && !usersCache.has(s.targetUserId)) {
          promises.push(fetchProfileByUserId(s.targetUserId));
        }
        if (s.targetPositionId && !positionsCache.has(s.targetPositionId)) {
          promises.push(fetchPositionById(s.targetPositionId));
        }
      });
      if (promises.length) {
        await Promise.allSettled(promises);
        setTick((t) => t + 1);
      }
    },
    []
  );

  const fetchProfileByUserId = useCallback(
    async (id: number): Promise<UserLite | null> => {
      if (usersCache.has(id)) return usersCache.get(id)!;
      try {
        const { data } = await axios.get(`${baseUrl}/profile/${id}`, {
          headers: tokenHeader(),
        });
        const userLite: UserLite = {
          id,
          username: data?.name || data?.email,
          email: data?.email,
          profile: {
            firstName: data?.firstName,
            lastName: data?.lastName,
            imageUrl: data?.imageUrl,
          },
        };
        usersCache.set(id, userLite);
        return userLite;
      } catch {
        return null;
      }
    },
    []
  );

  const fetchPositionById = useCallback(
    async (id: number): Promise<Position | null> => {
      if (positionsCache.has(id)) return positionsCache.get(id)!;
      try {
        const { data } = await axios.get(`${baseUrl}/positions/${id}`, {
          headers: tokenHeader(),
        });
        if (data?.id) {
          positionsCache.set(id, data);
          return data;
        }
      } catch {}
      return null;
    },
    []
  );

  const [tick, setTick] = useState(0);

  /** -------- Form Management -------- */
  const defaultRule: AssignmentRuleForm = {
    taskType: "",
    approvalPolicyId: "",
    name: "",
    isActive: true,
  };

  const defaultStep: AssignmentStepForm = {
    stepOrder: 1,
    assignmentType: "DEPARTMENT_MANAGERS",
    sameDepartmentAsRequester: false,
    useTaskProject: true,
    useProjectDepartment: true,
    autoAdvanceIfNone: false,
    isActive: true,
  };

  const {
    control: rc,
    handleSubmit: submitRule,
    reset: resetRule,
  } = useForm<AssignmentRuleForm>({
    defaultValues: defaultRule,
  });

  const {
    control: sc,
    handleSubmit: submitStep,
    reset: resetStep,
    watch,
    setValue,
  } = useForm<AssignmentStepForm>({
    defaultValues: defaultStep,
  });

  const assignmentType = watch("assignmentType");

  const openCreateRule = useCallback(() => {
    resetRule(defaultRule);
    setOpenRuleDialog(true);
  }, [resetRule]);

  const openEditRule = useCallback(
    (rule: AssignmentRule) => {
      resetRule({
        taskType: rule.taskType,
        approvalPolicyId: rule.approvalPolicyId,
        name: rule.name || "",
        isActive: rule.isActive,
      });
      setOpenRuleDialog(true);
    },
    [resetRule]
  );

  const openAddStep = useCallback(() => {
    const nextOrder = (selectedRule?.steps?.length ?? 0) + 1;
    resetStep({ ...defaultStep, stepOrder: nextOrder });
    setUserSearch("");
    setUsers([]);
    setOpenStepDialog({ open: true });
  }, [resetStep, selectedRule?.steps?.length]);

  const openEditStep = useCallback(
    async (step: AssignmentStep) => {
      // Extract target user IDs from the step (support both old and new format)
      const targetUserIds =
        (step as any).targetUsers?.map((tu: any) => tu.user?.id || tu.userId) ||
        [];
      const singleTargetUserId = step.targetUserId;

      resetStep({
        stepOrder: step.stepOrder,
        assignmentType: step.assignmentType,
        targetUserId: singleTargetUserId ?? null,
        targetUserIds:
          targetUserIds.length > 0
            ? targetUserIds
            : singleTargetUserId
            ? [singleTargetUserId]
            : [],
        targetRole: step.targetRole ?? "",
        targetPositionId: step.targetPositionId ?? null,
        sameDepartmentAsRequester: step.sameDepartmentAsRequester,
        useTaskProject: step.useTaskProject,
        useProjectDepartment: step.useProjectDepartment,
        autoAdvanceIfNone: step.autoAdvanceIfNone,
        isActive: step.isActive,
      });

      // Load users for multi-select
      if (step.assignmentType === "USER") {
        const userIdsToLoad =
          targetUserIds.length > 0
            ? targetUserIds
            : singleTargetUserId
            ? [singleTargetUserId]
            : [];

        if (userIdsToLoad.length > 0) {
          const loadPromises = userIdsToLoad.map((userId: number) =>
            !usersCache.has(userId)
              ? fetchProfileByUserId(userId)
              : Promise.resolve()
          );
          await Promise.all(loadPromises);

          const loadedUsers = userIdsToLoad
            .map((userId: number) => usersCache.get(userId))
            .filter(
              (u: UserLite | undefined): u is UserLite => u !== undefined
            );

          setUsers(loadedUsers);
          setUserSearch("");
        } else {
          setUsers([]);
          setUserSearch("");
        }
      } else {
        setUsers([]);
        setUserSearch("");
      }

      setOpenStepDialog({ open: true, stepId: step.id });
    },
    [fetchProfileByUserId, resetStep, setValue]
  );

  const onSubmitRule = submitRule(async (values) => {
    setBusy(true);
    try {
      const payload = {
        taskType: values.taskType?.trim() ?? "",
        approvalPolicyId: toInt(values.approvalPolicyId) ?? 0,
        name: values.name?.trim() || null,
        isActive: !!values.isActive,
      };

      if (selectedRuleId) {
        await axios.patch(
          `${baseUrl}/assignment-rules/${selectedRuleId}`,
          payload,
          {
            headers: tokenHeader(),
          }
        );
        notify("Assignment rule updated");
      } else {
        await axios.post(`${baseUrl}/assignment-rules`, payload, {
          headers: tokenHeader(),
        });
        notify("Assignment rule created");
      }

      setOpenRuleDialog(false);
      await loadRules();
    } catch (e) {
      handleErr(e, "Failed to save assignment rule");
    } finally {
      setBusy(false);
    }
  });

  const onSubmitStep = submitStep(async (values) => {
    if (!selectedRule) return;
    setBusy(true);
    try {
      const payload: any = {
        stepOrder: toInt(values.stepOrder) ?? 1,
        assignmentType: values.assignmentType,
        targetUserId:
          values.targetUserId === "" ||
          values.targetUserId === null ||
          values.targetUserId === undefined
            ? null
            : toInt(values.targetUserId) ?? null,
        targetRole:
          values.targetRole && values.targetRole.trim()
            ? values.targetRole.trim()
            : null,
        targetPositionId:
          values.targetPositionId === "" ||
          values.targetPositionId === null ||
          values.targetPositionId === undefined
            ? null
            : toInt(values.targetPositionId) ?? null,
        sameDepartmentAsRequester: !!values.sameDepartmentAsRequester,
        useTaskProject: !!values.useTaskProject,
        useProjectDepartment: !!values.useProjectDepartment,
        autoAdvanceIfNone: !!values.autoAdvanceIfNone,
        isActive: !!values.isActive,
      };

      // Handle multiple user IDs
      if (
        values.targetUserIds &&
        Array.isArray(values.targetUserIds) &&
        values.targetUserIds.length > 0
      ) {
        payload.targetUserIds = values.targetUserIds
          .map((id) => toInt(id))
          .filter((id): id is number => id !== undefined);
      }

      if (openStepDialog.stepId) {
        await axios.patch(
          `${baseUrl}/assignment-rules/steps/${openStepDialog.stepId}`,
          payload,
          {
            headers: tokenHeader(),
          }
        );
        notify("Assignment step updated");
      } else {
        await axios.post(
          `${baseUrl}/assignment-rules/${selectedRule.id}/steps`,
          payload,
          {
            headers: tokenHeader(),
          }
        );
        notify("Assignment step added");
      }
      setOpenStepDialog({ open: false });
      setUsers([]);
      setUserSearch("");
      await selectRule(selectedRule.id);
    } catch (e) {
      handleErr(e, "Failed to save assignment step");
    } finally {
      setBusy(false);
    }
  });

  const deleteStep = useCallback(
    async (stepId: number) => {
      if (!selectedRule) return;
      setBusy(true);
      try {
        await axios.delete(`${baseUrl}/assignment-rules/steps/${stepId}`, {
          headers: tokenHeader(),
        });
        notify("Assignment step deleted");
        await selectRule(selectedRule.id);
      } catch (e) {
        handleErr(e, "Failed to delete assignment step");
      } finally {
        setBusy(false);
      }
    },
    [handleErr, notify, selectRule, selectedRule]
  );

  const moveStep = useCallback(
    async (index: number, dir: -1 | 1) => {
      if (!selectedRule) return;
      const steps = [...(selectedRule.steps || [])].sort(
        (a, b) => a.stepOrder - b.stepOrder
      );
      if (index + dir < 0 || index + dir >= steps.length) return;
      const a = steps[index];
      const b = steps[index + dir];
      setBusy(true);
      try {
        await axios.patch(
          `${baseUrl}/assignment-rules/steps/${a.id}`,
          { stepOrder: b.stepOrder },
          { headers: tokenHeader() }
        );
        await axios.patch(
          `${baseUrl}/assignment-rules/steps/${b.id}`,
          { stepOrder: a.stepOrder },
          { headers: tokenHeader() }
        );
        await selectRule(selectedRule.id);
      } catch (e) {
        handleErr(e, "Failed to reorder steps");
      } finally {
        setBusy(false);
      }
    },
    [handleErr, selectRule, selectedRule]
  );

  /** -------- Ordered steps memo -------- */
  const orderedSteps = useMemo(
    () =>
      (selectedRule?.steps ?? [])
        .slice()
        .sort((a, b) => a.stepOrder - b.stepOrder),
    [selectedRule, tick]
  );

  /** -------- UI -------- */
  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={700}>
          Multi-Step Assignment Settings
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {busy && <CircularProgress size={24} />}
          <Button variant="outlined" onClick={loadRules}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateRule}
          >
            New Rule
          </Button>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        {/* Left: Rules list */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Assignment Rules
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1}>
                {rules.length === 0 && !busy && (
                  <Typography color="text.secondary">
                    No assignment rules yet.
                  </Typography>
                )}
                {busy && rules.length === 0 && (
                  <>
                    <Skeleton variant="rounded" height={64} />
                    <Skeleton variant="rounded" height={64} />
                  </>
                )}
                {rules.map((r) => (
                  <Card
                    key={r.id}
                    variant="outlined"
                    sx={{
                      p: 1,
                      borderColor:
                        selectedRuleId === r.id ? "primary.main" : undefined,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box
                        onClick={() => selectRule(r.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && selectRule(r.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Typography fontWeight={600}>{r.taskType}</Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Chip
                            size="small"
                            label={
                              r.approvalPolicy?.name ||
                              `Policy #${r.approvalPolicyId}`
                            }
                          />
                          <Chip
                            size="small"
                            label={`${r.steps?.length || 0} steps`}
                          />
                          <Chip
                            size="small"
                            color={r.isActive ? "success" : "default"}
                            label={r.isActive ? "Active" : "Inactive"}
                          />
                        </Stack>
                      </Box>
                      <Tooltip title="Edit rule">
                        <IconButton onClick={() => openEditRule(r)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Steps of selected rule */}
        <Grid item xs={12} md={8}>
          {!selectedRule ? (
            <Card>
              <CardContent>
                <Typography>
                  Select an assignment rule to manage its steps.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography variant="h6">
                    {selectedRule.taskType} — Assignment Steps
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openAddStep}
                  >
                    Add Step
                  </Button>
                </Stack>

                <Tabs
                  value={viewTab}
                  onChange={(_, v) => setViewTab(v)}
                  sx={{ mb: 2 }}
                >
                  <Tab
                    value="list"
                    icon={<ListAltIcon />}
                    iconPosition="start"
                    label="Steps List"
                  />
                  <Tab
                    value="diagram"
                    icon={<TimelineIcon />}
                    iconPosition="start"
                    label="Workflow Diagram"
                  />
                </Tabs>

                {viewTab === "list" && (
                  <Stack spacing={1}>
                    {orderedSteps.length === 0 && (
                      <Typography color="text.secondary">
                        No steps yet — add one.
                      </Typography>
                    )}
                    {orderedSteps.map((s, idx) => (
                      <StepCard
                        key={s.id}
                        step={s}
                        idx={idx}
                        total={orderedSteps.length}
                        dicts={{ users: usersCache, positions: positionsCache }}
                        onMove={moveStep}
                        onEdit={openEditStep}
                        onDelete={deleteStep}
                      />
                    ))}
                  </Stack>
                )}

                {viewTab === "diagram" && (
                  <Box sx={{ px: 1, py: 2 }}>
                    <Stepper orientation="vertical" activeStep={-1}>
                      {orderedSteps.map((s) => (
                        <Step key={s.id} expanded>
                          <StepLabel>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Chip
                                size="small"
                                label={`Step ${s.stepOrder}`}
                              />
                              <Chip
                                size="small"
                                color="primary"
                                label={s.assignmentType}
                              />
                              {s.autoAdvanceIfNone && (
                                <Chip size="small" label="Auto-advance" />
                              )}
                            </Stack>
                          </StepLabel>
                          <StepContent
                            TransitionProps={{ unmountOnExit: false }}
                          >
                            <StepCard
                              step={s}
                              idx={0}
                              total={1}
                              dicts={{
                                users: usersCache,
                                positions: positionsCache,
                              }}
                              onMove={() => {}}
                              onEdit={() => {}}
                              onDelete={() => {}}
                            />
                          </StepContent>
                        </Step>
                      ))}
                    </Stepper>
                    {orderedSteps.length === 0 && (
                      <Typography color="text.secondary">
                        No steps to display.
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Create/Edit Rule Dialog */}
      <Dialog
        open={openRuleDialog}
        onClose={() => setOpenRuleDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedRuleId ? "Edit Assignment Rule" : "New Assignment Rule"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Controller
              name="taskType"
              control={rc}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Task Type (e.g., LEAVE, EXPENSE_ADVANCE)"
                  fullWidth
                />
              )}
            />
            <Controller
              name="approvalPolicyId"
              control={rc}
              rules={{ required: true }}
              render={({ field }) => (
                <Autocomplete
                  options={policies}
                  loading={loadingPolicies}
                  onOpen={loadPolicies}
                  getOptionLabel={(o) => `${o.name} (${o.taskType})`}
                  isOptionEqualToValue={(o, v) => o.id === (v as any)?.id}
                  value={
                    policies.find((p) => p.id === toInt(field.value)) || null
                  }
                  onChange={(_, v) =>
                    field.onChange(v ? (v as ApprovalPolicy).id : "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Approval Policy"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingPolicies ? (
                              <CircularProgress size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="name"
              control={rc}
              render={({ field }) => (
                <TextField {...field} label="Rule Name (optional)" fullWidth />
              )}
            />
            <Controller
              name="isActive"
              control={rc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Active</Typography>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </Stack>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRuleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={onSubmitRule}>
            {selectedRuleId ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Step Dialog */}
      <Dialog
        open={openStepDialog.open}
        onClose={() => setOpenStepDialog({ open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {openStepDialog.stepId
            ? "Edit Assignment Step"
            : "Add Assignment Step"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Controller
              name="stepOrder"
              control={sc}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Step Order"
                  fullWidth
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === "" ? "" : toInt(e.target.value)
                    )
                  }
                  inputProps={{ step: 1, min: 1 }}
                />
              )}
            />
            <Controller
              name="assignmentType"
              control={sc}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField {...field} select label="Assignment Type" fullWidth>
                  {[
                    "USER",
                    "ROLE",
                    "POSITION",
                    "MANAGER_OF_REQUESTER",
                    "DEPARTMENT_MANAGERS",
                    "PROJECT_MANAGERS",
                  ].map((at) => (
                    <MenuItem key={at} value={at}>
                      {at}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {/* USER assignment */}
            {assignmentType === "USER" && (
              <Stack spacing={1.5}>
                {/* Filter Type Selector */}
                <TextField
                  select
                  size="small"
                  label="Filter users by"
                  value={userFilterType}
                  onChange={(e) => {
                    setUserFilterType(e.target.value as any);
                    setUserFilterDepartmentId(null);
                    setUserFilterProjectId(null);
                    setUserFilterPositionId(null);
                  }}
                  fullWidth
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="department">Department</MenuItem>
                  <MenuItem value="project">Project</MenuItem>
                  <MenuItem value="position">Position</MenuItem>
                </TextField>

                {/* Filter Value Selector */}
                {userFilterType === "department" && (
                  <Autocomplete
                    size="small"
                    options={departments}
                    getOptionLabel={(o) => o.name || `Department #${o.id}`}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={
                      departments.find(
                        (d) => d.id === userFilterDepartmentId
                      ) || null
                    }
                    onChange={(_, v) => {
                      setUserFilterDepartmentId(v ? v.id : null);
                      setUsers([]); // Clear users to trigger reload
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Department" />
                    )}
                  />
                )}

                {userFilterType === "project" && (
                  <Autocomplete
                    size="small"
                    options={projects}
                    getOptionLabel={(o) =>
                      o.title || o.name || `Project #${o.id}`
                    }
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={
                      projects.find((p) => p.id === userFilterProjectId) || null
                    }
                    onChange={(_, v) => {
                      setUserFilterProjectId(v ? v.id : null);
                      setUsers([]); // Clear users to trigger reload
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Project" />
                    )}
                  />
                )}

                {userFilterType === "position" && (
                  <Autocomplete
                    size="small"
                    options={positions}
                    getOptionLabel={(o) => posLabel(o)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={
                      positions.find((p) => p.id === userFilterPositionId) ||
                      null
                    }
                    onChange={(_, v) => {
                      setUserFilterPositionId(v ? v.id : null);
                      setUsers([]); // Clear users to trigger reload
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Position" />
                    )}
                  />
                )}

                <TextField
                  size="small"
                  placeholder="Search user by name/email…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                    ),
                  }}
                />

                <Controller
                  name="targetUserIds"
                  control={sc}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={users}
                      loading={loadingUsers}
                      getOptionLabel={(o) => userLabel(o)}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      value={
                        field.value
                          ? users.filter((u) => {
                              const fieldIds = Array.isArray(field.value)
                                ? field.value.map((id) => toInt(id))
                                : [];
                              return fieldIds.includes(u.id);
                            })
                          : []
                      }
                      onChange={(_, v) =>
                        field.onChange(v ? v.map((u: UserLite) => u.id) : [])
                      }
                       renderTags={(value, getTagProps) =>
                         value.map((option: UserLite, index: number) => (
                           <Chip
                             {...getTagProps({ index })}
                             key={option.id}
                             avatar={
                               <Avatar
                                 sx={{ width: 20, height: 20 }}
                                 src={option.profile?.imageUrl}
                                 alt={userLabel(option)}
                               >
                                 {avatarInitials(option)}
                               </Avatar>
                             }
                             label={userLabel(option)}
                           />
                         ))
                       }
                       renderOption={(props, option) => (
                         <li {...props} key={option.id}>
                           <Stack
                             direction="row"
                             spacing={1}
                             alignItems="center"
                           >
                             <Avatar
                               sx={{ width: 24, height: 24 }}
                               src={option.profile?.imageUrl}
                               alt={userLabel(option)}
                             >
                               {avatarInitials(option)}
                             </Avatar>
                             <Typography variant="body2">
                               {userLabel(option)}
                             </Typography>
                           </Stack>
                         </li>
                       )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select users (searchable, multi-select)"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingUsers ? (
                                  <CircularProgress size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Stack>
            )}

            {/* POSITION assignment */}
            {assignmentType === "POSITION" && (
              <Controller
                name="targetPositionId"
                control={sc}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    options={positions}
                    loading={loadingPositions}
                    onOpen={loadPositions}
                    getOptionLabel={(o) => posLabel(o)}
                    isOptionEqualToValue={(o, v) => o.id === (v as any)?.id}
                    value={
                      positions.find((p) => p.id === toInt(field.value)) || null
                    }
                    onChange={(_, v) =>
                      field.onChange(v ? (v as Position).id : "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Position"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingPositions ? (
                                <CircularProgress size={16} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />
            )}

            {/* ROLE assignment */}
            {assignmentType === "ROLE" && (
              <Controller
                name="targetRole"
                control={sc}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField {...field} label="Role (e.g., HR, GM)" fullWidth />
                )}
              />
            )}

            {/* Shared toggles */}
            <Controller
              name="sameDepartmentAsRequester"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Same Department As Requester</Typography>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </Stack>
              )}
            />
            <Controller
              name="useTaskProject"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Use Task Project</Typography>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </Stack>
              )}
            />
            <Controller
              name="useProjectDepartment"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Use Project Department</Typography>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </Stack>
              )}
            />
            <Controller
              name="autoAdvanceIfNone"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Auto-advance if no assignees found</Typography>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </Stack>
              )}
            />
            <Controller
              name="isActive"
              control={sc}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>Active</Typography>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </Stack>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStepDialog({ open: false })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSubmitStep}>
            {openStepDialog.stepId ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
